<script setup>
import { computed } from 'vue'

/**
 * A person's photograph, or their initials drawn locally.
 *
 * Everywhere in the product that needed a fallback avatar was building a URL at
 * ui-avatars.com — which sent the person's name to a third party on every
 * render, and put a network request in front of content that was already on the
 * page. Nothing about "two letters in a circle" needs a server.
 */
const props = defineProps({
  name: { type: String, default: '' },
  src: { type: String, default: null },
  /** Any Tailwind size utility pair, e.g. 'size-16'. */
  sizeClass: { type: String, default: 'size-10' },
  rounded: { type: String, default: 'rounded-full' },
})

const initials = computed(() => {
  const parts = (props.name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return (parts[0]?.[0] || '?').toUpperCase()
})
</script>

<template>
  <img
    v-if="src"
    :src="src"
    :alt="name"
    loading="lazy"
    :class="[sizeClass, rounded, 'border border-line object-cover']"
  />
  <span
    v-else
    :class="[sizeClass, rounded, 'grid place-items-center bg-brand-soft font-semibold text-brand-soft-text']"
    :aria-label="name || undefined"
    role="img"
  >{{ initials }}</span>
</template>
